import { action } from '@ember/object';
import { next, schedule } from '@ember/runloop';
import Component from '@glimmer/component';

import themed from 'webapp/decorators/themed';

type Value = unknown; // two-way bound to the caller's property (string, number, ...) via <Input @value>
export interface Args {
	value: Value;
	autofocus?: boolean;
	hideFloatLabel?: boolean;
	loseFocusOnEnter?: boolean;
	input?: (e: InputEvent) => void;
	focusIn?: (e: FocusEvent) => void;
	focusOut?: (e: FocusEvent) => void;
	insertNewline?: () => void;
	onEmpty?: (prevValue: Value) => void; // use set-field helper as this argument. we cannot change the @value here
	theme?: string;
	size?: string;
}

interface FormTextBaseComponentInterface<T> {
	Element: HTMLInputElement | HTMLTextAreaElement; // this is needed for ...attributes
	Args: T;
}

@themed()
export default abstract class FormTextBaseComponent<T extends Args> extends Component<
	FormTextBaseComponentInterface<T>
> {
	declare themeClass: string; // from @themed
	declare sizeClass: string; // from @themed

	originalValue: Value;

	get autofocus() {
		return this.args.autofocus ? 'on' : 'off';
	}

	@action
	input(e: Event) {
		// next, bcos this triggers before the setter for @value has executed
		next(() => {
			if (this.args.input) {
				this.args.input(e as InputEvent);
			}
		});
	}

	@action
	focusIn(e: FocusEvent) {
		this.originalValue = this.args.value;

		if (this.args.focusIn) {
			this.args.focusIn(e);
		}
	}

	@action
	focusOut(e: FocusEvent) {
		const trimmedValue = String(this.args.value || '').trim();

		if (!trimmedValue && this.args.onEmpty) {
			this.args.onEmpty(this.originalValue);
		}

		if (this.args.focusOut) {
			this.args.focusOut(e);
		}
	}

	@action
	keyDown(e: KeyboardEvent) {
		if (this.args.loseFocusOnEnter) {
			const pressedEnter = e.key === 'Enter';
			if (pressedEnter) {
				(e.target as HTMLInputElement).blur();
				e.stopPropagation();
			}
		}
	}

	@action
	insertNewline() {
		if (this.args.insertNewline) {
			this.args.insertNewline();
		}
	}

	@action
	setupView(elementNode: HTMLElement) {
		const node = elementNode as HTMLInputElement | HTMLTextAreaElement;
		if (!this.args.hideFloatLabel) {
			$(node).floatlabel();
		}
		if (this.args.autofocus) {
			schedule('afterRender', this, () => {
				node.focus();
				node.select();
			});
		}
	}
}
